import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * @author sheva2103
 * @project EasyMessager
 * @license MIT
 * @link https://github.com/sheva2103/EasyMessager
 * @email 2103sheva@gmail.com
 * @copyright (c) 2025 Aleksandr (GitHub: sheva2103)
 */
import { memo, useCallback, useEffect, useRef, useState } from "react";
import Avatar from "../Avatar/Avatar";
import classNames from "classnames";
import styles from './HomePage.module.scss';
import ContextMenu from "./ContextMenu";
import SelectMessageInput from "./SelectMessageInput";
import { useAppDispatch, useAppSelector } from "../../hooks/hook";
import { aggregateReactions, checkMessage, createNewDate, getTimeFromDate } from "../../utils/utils";
import UnreadIcon from '../../assets/check2.svg';
import ReadIcon from '../../assets/check2-all.svg';
import CallIcon from '../../assets/telephone-fill.svg';
import { messagesAPI } from "../../API/api";
import { useInView } from 'react-intersection-observer';
import { setTempChat } from "../../store/slices/appSlice";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { openModalCalls } from "../../store/slices/callsSlice";
import ImageDialog from "../Settings/ShowFullImages";
const ForwardedFrom = ({ user }) => {
    const dispatch = useAppDispatch();
    const currentUser = useAppSelector(state => state.app.currentUser);
    const selectedChat = useAppSelector(state => state.app.selectedChat);
    const { t } = useTypedTranslation();
    const name = (user === null || user === void 0 ? void 0 : user.channel) ? user.channel.displayName : user.displayName;
    const handleClick = (event) => {
        if ((user === null || user === void 0 ? void 0 : user.channel) || (user.uid !== currentUser.uid && user.uid !== selectedChat.uid))
            dispatch(setTempChat(user));
    };
    return (_jsxs("div", { className: styles.messageData__forwardedFrom, children: [_jsx("span", { children: `${t('forwardedFrom')}` }), _jsx("br", {}), _jsx("span", { style: { fontWeight: 500, textDecoration: 'underline' }, onClick: handleClick, id: "forwardedFromName", children: name })] }));
};
// const ImageLoader: FC<{ src: string | null, onClick: (e: React.MouseEvent) => void }> = ({ src, onClick }) => {
//     const [loaded, setLoaded] = useState(false);
//     if (!src) return null;
//     return (
//         <div className={styles.messageItem} onClick={onClick}>
//             {!loaded && <div className={styles.messageItem_skeleton} />}
//             <img
//                 src={src}
//                 alt="Загруженное изображение"
//                 onLoad={() => setLoaded(true)}
//                 onError={() => setLoaded(false)}
//                 style={{ display: loaded ? 'block' : 'none' }}
//                 className={styles.messageItem__image}
//             />
//         </div>
//     );
// };
const ImageLoader = ({ src, onClick }) => {
    const [loaded, setLoaded] = useState(false);
    if (!src)
        return null;
    return (_jsxs("div", { className: styles.messageItem, onClick: onClick, children: [_jsx("div", { className: `${styles.messageItem_skeleton} ${loaded ? styles.hidden : ''}` }), _jsx("img", { src: src, alt: "\u0417\u0430\u0433\u0440\u0443\u0436\u0435\u043D\u043D\u043E\u0435 \u0438\u0437\u043E\u0431\u0440\u0430\u0436\u0435\u043D\u0438\u0435", onLoad: () => setLoaded(true), onError: () => setLoaded(false), className: `${styles.messageItem__image} ${loaded ? styles.visible : ''}` })] }));
};
const YTPlayer = ({ src }) => {
    return (_jsx("div", { className: styles.messageData_playerConatiner, children: _jsx("iframe", { width: "100%", height: "100%", src: src, frameBorder: "0", allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture", allowFullScreen: true, title: "\u0412\u0441\u0442\u0440\u043E\u0435\u043D\u043D\u044B\u0439 YouTube-\u043F\u043B\u0435\u0435\u0440", style: {
                position: 'absolute',
                top: 0,
                left: 0,
                borderRadius: '16px'
            } }) }));
};
const ViewportContent = ({ onEnterViewport, message, currentUser }) => {
    var _a, _b;
    const { ref, inView } = useInView({
        threshold: 0.1,
    });
    const dispatch = useAppDispatch();
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const checkMessageObj = checkMessage(message.message);
    const { t } = useTypedTranslation();
    const status = message.callStatus;
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const clickShareChat = (e) => {
        e.stopPropagation();
        if (currentUser.uid !== message.shareChat.uid)
            dispatch(setTempChat(message.shareChat));
    };
    useEffect(() => {
        if (inView) {
            onEnterViewport();
        }
    }, [inView]);
    if (message === null || message === void 0 ? void 0 : message.callStatus) {
        return (_jsxs("div", { className: styles.call__info, children: [_jsxs("div", { className: styles.call__status, ref: ref, children: [_jsx(CallIcon, { fontSize: '0.9rem' }), _jsx("h5", { children: t(`call.${status}`) })] }), _jsx("span", { className: styles.call__time, children: message.message })] }));
    }
    if (message === null || message === void 0 ? void 0 : message.shareChat) {
        return (_jsx("div", { className: styles.messageData__replyToMessage, onClick: clickShareChat, ref: ref, children: _jsxs("div", { className: classNames(styles.messageData__replyToMessage_name, styles.messageData__shareChat), children: [_jsx(Avatar, { url: ((_a = message.shareChat) === null || _a === void 0 ? void 0 : _a.channel) ? (_b = message.shareChat) === null || _b === void 0 ? void 0 : _b.channel.photoURL : message.shareChat.photoURL, name: message.shareChat.displayName }), _jsx("span", { children: message.shareChat.displayName })] }) }));
    }
    const openFullImage = (e, index) => {
        e.stopPropagation();
        setCurrentIndex(index);
        setShowImage(true);
    };
    const closeFullImages = (e) => {
        e.stopPropagation();
        setShowImage(false);
    };
    return (_jsxs(_Fragment, { children: [_jsx("span", { dangerouslySetInnerHTML: { __html: checkMessageObj.message }, ref: ref, className: classNames({ [styles.mobileDevice]: isMobile, [styles.hasLink]: checkMessageObj.hasLink }) }), !!checkMessageObj.imgUrls.length &&
                _jsxs("div", { className: styles.messageData__list, children: [checkMessageObj.imgUrls.map((item, index) => _jsx(ImageLoader, { src: item, onClick: (e) => openFullImage(e, index) }, index)), _jsx(ImageDialog, { open: showImage, onClose: closeFullImages, images: checkMessageObj.imgUrls, startIndex: currentIndex })] }), !!checkMessageObj.YTUrls &&
                checkMessageObj.YTUrls.map((item, index) => _jsx(YTPlayer, { src: item }, index))] }));
};
const ReplyToMessage = (props) => {
    var _a;
    const { replyToMessage, sender } = props;
    const isMessageCall = replyToMessage === null || replyToMessage === void 0 ? void 0 : replyToMessage.callStatus;
    const { t } = useTypedTranslation();
    function getFirst30Chars(inputString) {
        if (!inputString)
            return '';
        if (inputString.length > 30) {
            return inputString.substring(0, 30) + '...';
        }
        return inputString;
    }
    return (_jsxs("div", { className: styles.messageData__replyToMessage, children: [_jsx("div", { className: classNames(styles.messageData__replyToMessage_name), children: _jsx("span", { children: ((_a = replyToMessage === null || replyToMessage === void 0 ? void 0 : replyToMessage.forwardedFrom) === null || _a === void 0 ? void 0 : _a.displayName) || (replyToMessage === null || replyToMessage === void 0 ? void 0 : replyToMessage.sender.displayName) }) }), _jsx("div", { style: { paddingRight: '2px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }, children: isMessageCall ?
                    _jsxs(_Fragment, { children: [_jsx(CallIcon, {}), _jsx("span", { children: t(`call.${replyToMessage.callStatus}`) })] })
                    :
                        _jsx("span", { children: getFirst30Chars(replyToMessage === null || replyToMessage === void 0 ? void 0 : replyToMessage.message) }) })] }));
};
const Reactions = ({ reactions, currentUser, chat, messageID }) => {
    const isFavorites = useAppSelector(state => state.app.isFavorites);
    const res = aggregateReactions(reactions, currentUser);
    const setReaction = (reaction, e) => {
        e.stopPropagation();
        messagesAPI.setReaction({
            reaction,
            chat,
            isMine: reaction.isMine,
            messageID,
            isFavorites
        });
    };
    if (!reactions || reactions.length === 0)
        return null;
    return (_jsx("div", { className: styles.messageData__reactions, children: res.map((r, i) => (_jsxs("div", { className: classNames(styles.reaction, { [styles.reaction__owner]: r.isMine }), children: [_jsx("span", { onClick: (e) => setReaction({ reaction: r.reaction, sender: currentUser, isMine: r.isMine }, e), children: r.reaction }), r.count > 1 && _jsx("span", { className: styles.reaction__count, children: r.count })] }, i))) }));
};
const Message = ({ messageInfo }) => {
    const owner = useAppSelector(state => state.app.currentUser);
    const isShowCheckbox = useAppSelector(state => state.app.showCheckbox);
    const chat = useAppSelector(state => state.app.selectedChat);
    const dispatch = useAppDispatch();
    const [contextMenuIsOpen, setContextMenu] = useState(false);
    //const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isOwner = owner.email === messageInfo.sender.email;
    const isForwarder = Boolean(messageInfo === null || messageInfo === void 0 ? void 0 : messageInfo.forwardedFrom);
    const isFavorites = messageInfo.hasOwnProperty('read');
    const isGuestMessage = messageInfo.sender.uid !== owner.uid;
    const isCallMessage = !!(messageInfo === null || messageInfo === void 0 ? void 0 : messageInfo.callStatus);
    const statusCallMessage = () => {
        if (messageInfo.callStatus === 'completed')
            return styles.call_completed;
        return styles.call_error;
    };
    const messageRef = useRef(null);
    const refSpan = useRef(null);
    const [positionClick, setPositionClick] = useState({ top: 0, left: 0 });
    const setPositionMenu = useCallback((e) => {
        if (!isShowCheckbox) {
            const top = e.clientY;
            const left = e.clientX;
            setPositionClick({ top, left });
        }
    }, [isShowCheckbox]);
    const mobileHandleClick = (e) => {
        const targetElement = e.target;
        if (isShowCheckbox && targetElement.tagName === 'A') {
            e.preventDefault();
        }
        if (!isMobile) {
            if ((messageInfo === null || messageInfo === void 0 ? void 0 : messageInfo.callStatus) && !isShowCheckbox) {
                dispatch(openModalCalls({
                    mode: null,
                    callerUid: chat.uid,
                    roomId: null,
                    callInfo: { caller: owner, callee: chat }
                }));
            }
            return;
        }
        if (targetElement.nodeName !== 'A' && !isShowCheckbox && targetElement.id !== 'forwardedFromName')
            setContextMenu(true);
    };
    const openContextMenu = (e) => {
        if (isMobile)
            return;
        e.preventDefault();
        if (!isShowCheckbox)
            setContextMenu(true);
    };
    const closeContextMenu = (e) => {
        setContextMenu(false);
    };
    useEffect(() => {
        if (refSpan.current) {
            // refSpan.current.addEventListener('contextmenu', setPositionMenu)
            // refSpan.current.addEventListener('click', setPositionMenuForIOS)
            refSpan.current.addEventListener('contextmenu', setPositionMenu);
            refSpan.current.addEventListener('click', setPositionMenu);
            return () => {
                var _a, _b;
                // refSpan.current?.removeEventListener('contextmenu', setPositionMenu)
                // refSpan.current?.removeEventListener('click', setPositionMenuForIOS)
                (_a = refSpan.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('contextmenu', setPositionMenu);
                (_b = refSpan.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('click', setPositionMenu);
            };
        }
    }, [isShowCheckbox]);
    const readMessage = () => {
        if (messageInfo.sender.email !== owner.email && isFavorites && !messageInfo.read) {
            messagesAPI.readMessage(chat, messageInfo);
        }
    };
    console.log('message render');
    return (_jsx("li", { className: classNames({ [styles.selectedMessage]: contextMenuIsOpen }), ref: messageRef, children: _jsxs("label", { "data-id": messageInfo.messageID, children: [isShowCheckbox && _jsx(SelectMessageInput, { messageInfo: messageInfo }), _jsx("div", { className: styles.avatar, children: messageInfo.sender.email === owner.email ?
                        _jsx(Avatar, { url: owner === null || owner === void 0 ? void 0 : owner.photoURL, name: owner.displayName })
                        :
                            _jsx(Avatar, { url: chat === null || chat === void 0 ? void 0 : chat.photoURL, name: chat.displayName }) }), _jsxs("div", { className: classNames(styles.messageData, {
                        [styles.owner]: isGuestMessage,
                        [styles.guest]: !isGuestMessage,
                        [styles.noSelect]: contextMenuIsOpen,
                        [statusCallMessage()]: isCallMessage,
                        [styles.call]: isCallMessage
                    }), onContextMenu: openContextMenu, onClick: mobileHandleClick, ref: refSpan, children: [messageInfo.forwardedFrom && _jsx(ForwardedFrom, { user: messageInfo.forwardedFrom }), messageInfo.replyToMessage && _jsx(ReplyToMessage, Object.assign({}, messageInfo)), _jsx(ViewportContent, { onEnterViewport: readMessage, message: messageInfo, currentUser: owner }), _jsxs("div", { className: styles.messageData__info, children: [_jsx(Reactions, { reactions: messageInfo === null || messageInfo === void 0 ? void 0 : messageInfo.reactions, currentUser: owner, chat: chat, messageID: messageInfo.messageID }), _jsxs("div", { className: styles.infoWrapper, children: [_jsx("div", { className: styles.messageData__date, children: _jsx("span", { children: messageInfo.changed ? `ред.${getTimeFromDate(createNewDate(messageInfo.changed))}` : getTimeFromDate(createNewDate(messageInfo.date)) }) }), messageInfo.sender.email === owner.email && isFavorites &&
                                            _jsx("div", { className: styles.messageData__status, children: messageInfo.read ? _jsx(ReadIcon, {}) : _jsx(UnreadIcon, {}) })] })] })] }), contextMenuIsOpen &&
                    _jsx(ContextMenu, { isOpen: contextMenuIsOpen, closeContextMenu: closeContextMenu, isOwner: isOwner, message: messageInfo, isForwarder: isForwarder, currentUser: owner, positionClick: positionClick })] }) }));
};
function checkProps(prevProps, nextProps) {
    var _a, _b;
    return prevProps.messageInfo.message === nextProps.messageInfo.message
        &&
            prevProps.messageInfo.read === nextProps.messageInfo.read
        &&
            ((_a = prevProps.messageInfo) === null || _a === void 0 ? void 0 : _a.reactions) === ((_b = nextProps.messageInfo) === null || _b === void 0 ? void 0 : _b.reactions);
}
export default memo(Message, checkProps);
